import { TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { AppConfigService } from './app.config.service';
import { ConfigurationService } from './core/services/configurationService';
import { throwError } from 'rxjs/internal/observable/throwError';
import { FeatureType } from '@shared/type/feature.type';
import { DynatraceLogService } from '@coreModule/services/dynatraceLog.service';
import { DynatraceLogActionName } from '@shared/enum/dynatraceLogActionName.enum';
import { IRSACaseConfig } from '@rsaCaseV2/interfaces/IRSACaseConfig';
import { FeaturePath } from '@shared/enum/featurePath.enum';

@Injectable()
export class TranslateCustomLoader implements TranslateLoader {
  public actionId: number;
  public xhrActionId: number;

  constructor(private http: HttpClient, private appConfigService: AppConfigService, public dynatraceLogService: DynatraceLogService) {
    this.setBCEFlag();
  }

  public getTranslation(lang: string): Observable<any> {
    // Get translation from Bucket
    const enabledS3 = this.appConfigService?.settings?.enabledS3;
    const draftTsFlag = JSON.parse(sessionStorage.getItem('draftTs'));
    const finalTsFlag = JSON.parse(sessionStorage.getItem('finalTs'));

    const endpoint = this.computeEndpoint(lang, enabledS3, draftTsFlag, finalTsFlag);
    const urlPath = this.computePath(lang, enabledS3, draftTsFlag || finalTsFlag);

    return this.http.get(`${endpoint}${urlPath}`).pipe(
      catchError((err) => {
        this.dynatraceLogService?.setAutomaticActionDetection(false);
        this.dynatraceLogService?.enterAction(DynatraceLogActionName.LOAD_TRANSLATION);
        this.dynatraceLogService?.addActionProperties('action', {
          url: {
            value: `${endpoint}${urlPath}`,
            public: true
          }
        });
        this.dynatraceLogService.dynatraceLogClear();
        setTimeout(() => {
          this.redirect('500.html');
        }, 1000);
        return throwError(() => err);
      })
    );
  }

  getCurrentURL(): string {
    return window.location.href.split('?')[0];
  }

  redirect(url: string): void {
    window.location.replace(url);
  }

  private setBCEFlag(): void {
    const params = sessionStorage.getItem('urlParameters');
    if (params === null) {
      return;
    }
    const paramsArray = params.split('&');
    paramsArray.forEach((item) => {
      const paramKey = item.split('=')[0];
      const paramValue = item.split('=')[1];
      if (paramKey == 'draftTs' || paramKey == 'finalTs') {
        sessionStorage.setItem(paramKey, paramValue);
      }
    });
  }

  private computeEndpoint(lang: string, enabledS3: boolean, draftTsFlag: boolean, finalTsFlag: boolean) {
    if (draftTsFlag) {
      return this.appConfigService?.settings?.draftBucketURL;
    } else if (finalTsFlag) {
      return this.appConfigService?.settings?.finalBucketURL;
    }
    const { useS3URL, useGatewayURL } = this.useAlternativeUrl(lang);
    const useS3Endpoint = enabledS3 && useS3URL && !useGatewayURL;
    if (useS3Endpoint) {
      return this.appConfigService?.settings?.finalBucketURL;
    } else if (useGatewayURL) {
      return this.appConfigService?.settings?.gatewayURL;
    }
    return this.appConfigService?.settings?.endpoint;
  }

  private computePath(lang: string, enabledS3: boolean, bceFlag: boolean) {
    const { useS3URL, useGatewayURL } = this.useAlternativeUrl(lang);
    const { textRoute, textRouteS3 } = this.useAlternativeRoute(lang);
    const featureEnabledS3 = enabledS3 && useS3URL && !useGatewayURL;
    if (bceFlag || featureEnabledS3) {
      return textRouteS3;
    }
    return textRoute;
  }

  private useAlternativeUrl(lang: string) {
    let useGatewayURL, useS3URL;
    const token = sessionStorage.getItem('token');
    const featureS3: FeatureType[] = ['case', 'tracking', 'rating', 'vb-webchat'];
    const featureGateway: FeatureType[] = ['extra-payment', 'follow-up'];
    const textRoute = token ? this.getPathFromAuthData(lang) : this.getPathFromURL(lang);
    if (ConfigurationService?.authData) {
      useS3URL = !!featureS3.find((f) => f === ConfigurationService.authData.feature);
      useGatewayURL = !!featureGateway.find((f) => f === ConfigurationService.authData.feature);
    } else {
      if (textRoute.includes('/text-case/') || textRoute.includes('/text-webchat/')) {
        useS3URL = true;
      } else if (textRoute.includes('/text-follow-up/')) {
        useGatewayURL = true;
      }
    }
    return { useS3URL, useGatewayURL };
  }

  private useAlternativeRoute(lang: string) {
    const token = sessionStorage.getItem('token');
    const tenant = sessionStorage.getItem('tenant')?.toLowerCase();
    const partnerId = sessionStorage.getItem('PartnerID')?.toUpperCase();
    const tenantPartner = partnerId ? `${tenant}/${partnerId}/` : `${tenant}/`;
    const translationKeyPath = token ? this.getTranslationS3PathFromAuthData() : this.getTranslationS3PathFromNonAuthData();
    const textRouteS3 = `/translation/${translationKeyPath}/${tenantPartner}${lang}.json`;
    const textRoute = token ? this.getPathFromAuthData(lang) : this.getPathFromURL(lang);
    return { textRoute, textRouteS3 };
  }

  private getTranslationS3PathFromAuthData(): string {
    let { lob, feature } = ConfigurationService.authData;
    if (feature === 'case') {
      const enableNewUxUi = (ConfigurationService.appConfig as IRSACaseConfig).general?.enableNewUxUi;
      feature = enableNewUxUi ? 'case-v2' : 'case';
    }
    return `${lob}/${feature}`;
  }

  private getTranslationS3PathFromNonAuthData(): string {
    const urlWithoutParams = this.getCurrentURL();
    const isFollowup = urlWithoutParams.includes('followup');
    const isVbWebchat = urlWithoutParams.includes('vbwebchat');

    let lob = 'rsa';
    if (urlWithoutParams.includes('/home/')) {
      lob = 'home';
    }

    let feature = 'case';
    if (isFollowup) {
      feature = 'follow-up';
    } else if (isVbWebchat) {
      feature = 'vb-webchat';
    } else {
      const enableNewUxUi = (ConfigurationService.appConfig as IRSACaseConfig)?.general?.enableNewUxUi;
      feature = enableNewUxUi ? 'case-v2' : 'case';
    }
    return `${lob}/${feature}`;
  }

  private getPathFromAuthData(lang: string): string {
    const { feature, lob } = ConfigurationService.authData;
    const textType = `/text-${feature}`;
    const tenant = ConfigurationService.authData?.tenant?.toLowerCase();
    const partnerId = ConfigurationService.authData?.partnerId?.toUpperCase();
    const tenantPartner = partnerId ? `${tenant}/${partnerId}/` : `${tenant}/`;

    if (feature === 'vb-webchat' || feature === 'reimbursement') {
      return `${textType}/${lang}`;
    }

    if (feature === 'tracking' || feature === 'rating') {
      return `/text-case/${lob}/${tenantPartner}${lang}`;
    }

    // feature :: case, extra-payment, follow-up
    return `${textType}/${lob}/${tenantPartner}${lang}`;
  }

  private getPathFromURL(lang: string): string {
    const urlWithoutParams = this.getCurrentURL();
    const rsaReimbursement = urlWithoutParams.includes('rsa/reimbursement');
    const homeReimbursement = urlWithoutParams.includes('home/reimbursement');
    const followup = urlWithoutParams.includes('followup');
    const rsaVbWebchat = urlWithoutParams?.toLowerCase()?.includes(FeaturePath.RSA_VB_WEBCHAT);
    const homeVbWebchat = urlWithoutParams?.toLowerCase()?.includes(FeaturePath.HOME_VB_WEBCHAT);
    const lob = urlWithoutParams.includes('rsa') ? 'rsa' : 'home';
    const tenant = sessionStorage.getItem('tenant')?.toLowerCase();
    const partnerId = sessionStorage.getItem('PartnerID')?.toUpperCase();
    const tenantPartner = partnerId ? `${tenant}/${partnerId}/` : `${tenant}/`;
    let textType = '/text-case/rsa/';

    if (followup) {
      textType = `/text-follow-up/${lob}/${tenant}/`;
    } else if (rsaReimbursement || homeReimbursement) {
      textType = '/text-reimbursement/';
    } else if (rsaVbWebchat || homeVbWebchat) {
      textType = `/text-webchat/${lob}/${tenantPartner}`;
    } else {
      return `${textType}${tenantPartner}${lang}`;
    }
    return `${textType}${lang}`;
  }
}
